import React from 'react';
import classNames from 'classnames';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import styles from './Notification.module.scss';
import Icon from '../../Icon/Icon';
import Button, { ButtonToneProps } from '../../Button/Button';

dayjs.extend(utc);
dayjs.extend(timezone);

export type NotificationListItemProps = {
  packageId: number;
  reference?: string;
  name: string;
  details?: string;
  date: string;
  onClick?: () => void;
  isReturnPackage?: boolean;
  read?: boolean;
  warning?: boolean;
};

const getFormattedDate = (dateString: string, t: TFunction): string => {
  // Convert the date to the local timezone
  const notificationDate = dayjs.utc(dateString).local();
  const now = dayjs();
  const diffInMinutes = now.diff(notificationDate, 'minute');
  const diffInHours = now.diff(notificationDate, 'hour');
  const isCurrentYear = notificationDate.isSame(now, 'year');

  if (diffInMinutes < 60) {
    return t('notifications.minutes_ago', { count: diffInMinutes });
  }
  if (diffInHours < 24) {
    return t('notifications.hours_ago', { count: diffInHours });
  }
  if (notificationDate.isSame(now, 'day')) {
    return t('notifications.today');
  }
  if (notificationDate.isSame(now.subtract(1, 'day'), 'day')) {
    return t('notifications.yesterday');
  }

  return notificationDate.format(isCurrentYear ? 'D. MMM' : 'D. MMM YYYY');
};

const Notification: React.FC<NotificationListItemProps> = ({
  packageId,
  reference,
  name,
  details,
  date,
  read,
  warning,
  isReturnPackage,
  onClick,
}) => {
  const { t } = useTranslation();
  const formattedDate = getFormattedDate(date, t);

  return (
    <div className={classNames(styles.root)} onClick={onClick} onKeyDown={onClick} role="presentation">
      <div className="flex justify-between items-center">
        <div className="flex">
          {warning && <Icon name="warning" width={20} height={20} className="pr-4 status-warning-full" />}
          <span className="text-full typo-text-14--bold ">{name}</span>
        </div>
        <span
          className={classNames(
            { 'status-warning-full': !read, 'status-neutral-1-full': read },
            'typo-text-12--bold flex items-center',
          )}>
          {formattedDate} <span className={classNames(styles.dot)} />
        </span>
      </div>
      {details && <span className="text-faded typo-text-12 mt-8">{details}</span>}
      <div className="mt-6">
        <Button
          className="max-w-full"
          title={reference || packageId.toString()}
          tone={ButtonToneProps.SECONDARY}
          icon={isReturnPackage ? 'returns' : 'package'}
        />
      </div>
    </div>
  );
};

export default Notification;
